// Guards against infinite reload loops. The reactive recovery paths
// (vite:preloadError, dynamic-import retry exhaustion) reload the page to pick
// up freshly deployed assets. If the served HTML is itself stale, reloading
// could loop forever. This caps reloads within a short window.

const STORAGE_KEY = 'otm-reload-guard';
const MAX_RELOADS = 3;
const WINDOW_MS = 30 * 1000; // 30 seconds

/**
 * Reloads the page unless we have already reloaded too many times recently.
 * Returns `true` if a reload was triggered, `false` if it was suppressed.
 */
export function safeReload(): boolean {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(STORAGE_KEY);
    let count = 0;
    let first = now;

    if (raw) {
      const parsed = JSON.parse(raw) as { count: number; first: number };
      if (now - parsed.first < WINDOW_MS) {
        count = parsed.count;
        first = parsed.first;
      }
    }

    if (count >= MAX_RELOADS) {
      // Too many reloads in the window; stop to avoid an infinite loop.
      return false;
    }

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ count: count + 1, first }));
  } catch {
    // If sessionStorage is unavailable, fall through and reload anyway.
  }

  window.location.reload();
  return true;
}
